import React, { useRef, useState, useEffect } from 'react';
import { AgCharts } from 'ag-charts-react';
import { Col, Row } from 'react-bootstrap';

interface ChartDataItem {
    category: string;
    value: number;
    fill: string;
}

interface DonutChartProps {
    data: ChartDataItem[];
    title?: string;
    centerText?: {
        label: string;
        value: string | number;
    };
    height?: number;
    width?: number;
    innerRadiusRatio?: number;
    outerRadiusRatio?: number;
    showLegend?: boolean;
    showCustomLegend?: boolean;
    showLabels?: boolean;
    showCalloutLabels?: boolean;
    showTooltip?: boolean;
    colors?: string[];
    className?: string;
    style?: React.CSSProperties;
    onSegmentClick?: (data: ChartDataItem) => void;
}

export default function DonutChart({
    data,
    title = "",
    centerText,
    height = 400,
    width = 400,
    innerRadiusRatio = 0.6,
    outerRadiusRatio = 0.9,
    showLegend = false,
    showCustomLegend = false,
    showLabels = false,
    showCalloutLabels = false,
    showTooltip = true,
    colors,
    className = "",
    style = {},
    onSegmentClick
}: DonutChartProps) {
    
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(width || 400);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const w = entry.contentRect.width;
                if (w > 0) setContainerWidth(w);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const filteredData = data.filter(item => item.value > 0);

    const chartData = filteredData.map((item, index) => ({
        ...item,
        fill: colors ? colors[index] || item.fill : item.fill
    }));

    const chartOptions: any = {
        data: chartData,
        title: {
            text: title,
        },
        height,
        width: containerWidth,
        series: [
            {
                type: "donut",
                calloutLabelKey: "category",
                angleKey: "value",
                innerRadiusRatio,
                outerRadiusRatio,
                fills: chartData.map((item) => item.fill),
                calloutLabel: {
                    enabled: showCalloutLabels
                },
                sectorLabel: {
                    enabled: showLabels
                },
                tooltip: showTooltip ? {
                    renderer: (params: any) => {
                        return {
                            content: `${params.datum.category}: ${params.datum.value.toLocaleString()}`
                        };
                    }
                } : { enabled: false },
                listeners: onSegmentClick ? {
                    nodeClick: (event: any) => {
                        if (event.datum) {
                            onSegmentClick(event.datum);
                        }
                    }
                } : undefined
            },
        ],
        legend: {
            enabled: showLegend,
            position: "bottom",
            spacing: 40,
            item: {
                marker: {
                    shape: "circle",
                    size: 8
                },
                label: {
                    fontSize: 14,
                    fontWeight: "normal",
                    color: "#666"
                },
                paddingX: 16,
                paddingY: 8
            }
        },
        background: {
            fill: "transparent"
        },
        padding: {
            top: 20,
            right: 20,
            bottom: 20,
            left: 20
        }
    };

    return (
        <div className={className} style={style} ref={containerRef}>
            <Row className="justify-content-center">
                <Col xs={12}>
                    <div className="d-flex justify-content-center">
                        <div className="position-relative d-inline-block" style={{ width: '100%' }}>
                            <AgCharts options={chartOptions} />
                            
                            {centerText && (
                                <div 
                                    className="position-absolute top-50 start-50 translate-middle text-center"
                                    style={{ 
                                        pointerEvents: 'none',
                                        zIndex: 10
                                    }}
                                >
                                    <div className="text-muted" style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '500' }}>
                                        {centerText.label}
                                    </div>
                                    <div className="fw-bold" style={{ fontSize: '32px', color: '#2c3e50', lineHeight: '1' }}>
                                        {typeof centerText.value === 'number' ? centerText.value.toLocaleString() : centerText.value}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {showCustomLegend && (
                        <div className="mt-4">
                            <Row className="justify-content-center">
                                <Col md={8}>
                                    <div className="d-flex justify-content-around text-center">
                                        {chartData.map((item, index) => (
                                            <div key={index} className="d-flex flex-column align-items-center">
                                                <div className="d-flex align-items-center mb-2">
                                                    <div 
                                                        className="me-2" 
                                                        style={{ 
                                                            width: '16px', 
                                                            height: '16px', 
                                                            backgroundColor: item.fill, 
                                                            borderRadius: '50%' 
                                                        }}
                                                    ></div>
                                                    <span className="text-muted fw-medium" style={{ fontSize: '16px' }}>
                                                        {item.category}
                                                    </span>
                                                </div>
                                                <div className="fw-bold" style={{ fontSize: '24px', color: item.fill }}>
                                                    {item.value.toLocaleString()}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
}

// Export types for use in other components
export type { ChartDataItem, DonutChartProps };
